/**
 * Auth bridge for `<legion-*>` web components (plan §4.2, LEG-444).
 *
 * Wires three message paths around an iframe-backed widget:
 *
 *   parent page  --(window.postMessage 'legion:auth')-->  host component
 *   host         --(iframe.postMessage 'AUTH_TOKEN')---->  iframe
 *   iframe       --(window.postMessage 'legion:auth-expired')->  host component
 *                                                                      └─ re-emitted as
 *                                                                         CustomEvent('legion:auth-expired')
 *
 * Token sources, highest to lowest precedence:
 *   1. parent postMessage `{ type: "legion:auth", token }`
 *   2. `auth-token` attribute (canonical, plan §4.2)
 *   3. `api-key` attribute  (legacy alias, kept for backwards-compat)
 *
 * Parent → host messages are filtered against the comma-separated
 * `allowed-parent-origins` attribute. If unset, only `window.parent === self`
 * (top-level page) and same-origin parents are accepted.
 */

export interface AuthBridgeOptions {
  /** Returns the live iframe element if mounted. */
  iframeAccessor: () => HTMLIFrameElement | null;
  /** Used as the targetOrigin when posting to the iframe. */
  baseUrl: string;
}

export interface AuthBridgeHandle {
  /** Tear down listeners. Call from `disconnectedCallback`. */
  detach(): void;
  /** Push the current token to the iframe. Call after `iframe.onload`. */
  postCurrentToken(): void;
  /** Resolve the active token (parent override → auth-token → api-key). */
  currentToken(): string | null;
}

const AUTH_TOKEN_MESSAGE = "AUTH_TOKEN";
const PARENT_AUTH_MESSAGE = "legion:auth";
const IFRAME_EXPIRED_MESSAGE = "legion:auth-expired";

function parseAllowedOrigins(host: HTMLElement): string[] {
  const raw = host.getAttribute("allowed-parent-origins") || "";
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function isAllowedParentOrigin(host: HTMLElement, origin: string): boolean {
  // Same-origin is always trusted.
  if (origin === window.location.origin) return true;
  const allowed = parseAllowedOrigins(host);
  return allowed.includes(origin);
}

export function attachAuthBridge(
  host: HTMLElement,
  opts: AuthBridgeOptions
): AuthBridgeHandle {
  // Token override pushed by the parent via postMessage. Wins over attrs.
  let parentToken: string | null = null;

  const currentToken = (): string | null => {
    if (parentToken) return parentToken;
    return host.getAttribute("auth-token") || host.getAttribute("api-key");
  };

  const postCurrentToken = (): void => {
    const iframe = opts.iframeAccessor();
    const token = currentToken();
    if (!iframe || !iframe.contentWindow || !token) return;
    iframe.contentWindow.postMessage(
      { type: AUTH_TOKEN_MESSAGE, token },
      opts.baseUrl
    );
  };

  const onMessage = (event: MessageEvent): void => {
    if (!event || typeof event.data !== "object" || event.data === null) {
      return;
    }
    const data = event.data as { type?: unknown; token?: unknown };

    // 1. Parent → host: token refresh.
    if (data.type === PARENT_AUTH_MESSAGE) {
      if (!isAllowedParentOrigin(host, event.origin)) return;
      if (typeof data.token !== "string" || !data.token) return;
      parentToken = data.token;
      postCurrentToken();
      return;
    }

    // 2. Iframe → host: token expired. The iframe is mounted in the
    // shadow DOM; messages from it carry `event.source === iframe.contentWindow`.
    if (data.type === IFRAME_EXPIRED_MESSAGE) {
      const iframe = opts.iframeAccessor();
      if (!iframe || event.source !== iframe.contentWindow) return;
      host.dispatchEvent(
        new CustomEvent(IFRAME_EXPIRED_MESSAGE, {
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  window.addEventListener("message", onMessage);

  return {
    detach() {
      window.removeEventListener("message", onMessage);
    },
    postCurrentToken,
    currentToken,
  };
}

/** Attribute names the bridge cares about; widgets must add these to `observedAttributes`. */
export const AUTH_BRIDGE_OBSERVED_ATTRS = [
  "auth-token",
  "api-key",
  "allowed-parent-origins",
] as const;
